/**
 * Platform Detection Service
 * Detects WeChat browser and provides platform-specific solutions
 */

export interface PlatformInfo {
  isWeChat: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isMobile: boolean;
  browserName: string;
  supportsRecording: boolean;
  recordingSolution: string;
}

export class PlatformService {
  /**
   * Detect current platform
   */
  detect(): PlatformInfo {
    const ua = navigator.userAgent.toLowerCase();

    const isWeChat = ua.includes('micromessenger');
    const isIOS = ua.includes('iphone') || ua.includes('ipad');
    const isAndroid = ua.includes('android');
    const isMobile = isIOS || isAndroid;

    // Detect browser
    let browserName = 'Unknown';
    if (isWeChat) {
      browserName = 'WeChat';
    } else if (ua.includes('chrome')) {
      browserName = 'Chrome';
    } else if (ua.includes('safari')) {
      browserName = 'Safari';
    } else if (ua.includes('firefox')) {
      browserName = 'Firefox';
    } else if (ua.includes('edge')) {
      browserName = 'Edge';
    }

    // Check recording support
    const supportsRecording = this.checkRecordingSupport(isWeChat, isIOS);

    // Get solution message
    const recordingSolution = this.getRecordingSolution(isWeChat, isIOS, isAndroid);

    return {
      isWeChat,
      isIOS,
      isAndroid,
      isMobile,
      browserName,
      supportsRecording,
      recordingSolution,
    };
  }

  /**
   * Check if recording is supported
   */
  private checkRecordingSupport(isWeChat: boolean, isIOS: boolean): boolean {
    // WeChat iOS: No support
    if (isWeChat && isIOS) {
      return false;
    }

    // WeChat Android: Partial support (needs permission)
    if (isWeChat) {
      return false; // Conservative: mark as unsupported
    }

    // Other browsers: Check API availability
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const hasGetUserMedia = !!navigator.mediaDevices?.getUserMedia;

    return !!SpeechRecognition && hasGetUserMedia;
  }

  /**
   * Get platform-specific recording solution
   */
  private getRecordingSolution(isWeChat: boolean, isIOS: boolean, isAndroid: boolean): string {
    if (!isWeChat) {
      return '✅ 当前浏览器支持录音功能';
    }

    // WeChat iOS
    if (isIOS) {
      return `⚠️ 微信浏览器不支持录音功能

解决方案：
1️⃣ 点击右上角「...」菜单
2️⃣ 选择「在浏览器打开」
3️⃣ 使用Safari浏览器打开
4️⃣ 即可正常录音学习

提示：iOS微信限制了录音功能，请在Safari中使用。`;
    }

    // WeChat Android
    if (isAndroid) {
      return `⚠️ 微信浏览器录音功能受限

解决方案：
1️⃣ 点击右上角「...」菜单
2️⃣ 选择「在浏览器打开」
3️⃣ 使用Chrome浏览器打开
4️⃣ 即可正常录音学习

或者：
- 在微信中继续浏览其他功能
- 录音练习请在外部浏览器使用`;
    }

    // WeChat (generic)
    return `⚠️ 微信浏览器不支持录音功能

建议：
- 点击右上角菜单 → 在浏览器打开
- 使用系统浏览器访问本应用`;
  }

  /**
   * Show recording guide modal
   */
  showRecordingGuide(): void {
    const platform = this.detect();

    if (platform.supportsRecording) {
      return; // No need to show guide
    }

    // Show WeChat guide
    if (platform.isWeChat) {
      const message = platform.recordingSolution;

      // Create modal
      const modal = document.createElement('div');
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        padding: 20px;
      `;

      modal.innerHTML = `
        <div style="
          background: white;
          padding: 30px;
          border-radius: 20px;
          max-width: 400px;
          text-align: center;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        ">
          <div style="font-size: 60px; margin-bottom: 20px;">📱</div>
          <h3 style="margin: 0 0 15px 0; color: #333;">录音功能使用提示</h3>
          <p style="margin: 0 0 20px 0; color: #666; font-size: 14px; line-height: 1.6; white-space: pre-line;">
            ${message}
          </p>
          <button onclick="this.parentElement.parentElement.remove()" style="
            background: #4caf50;
            color: white;
            border: none;
            padding: 12px 30px;
            border-radius: 25px;
            font-size: 16px;
            cursor: pointer;
          ">
            我知道了
          </button>
        </div>
      `;

      document.body.appendChild(modal);
    }
  }

  /**
   * Check if should block recording feature
   */
  shouldBlockRecording(): boolean {
    const platform = this.detect();
    return !platform.supportsRecording;
  }

  /**
   * Get WeChat open in browser guide
   */
  getWeChatOpenGuide(): string {
    return `
如何在外部浏览器打开：

iPhone用户：
1. 点击右上角「...」
2. 选择「在Safari中打开」
3. 即可使用完整录音功能

Android用户：
1. 点击右上角「...」
2. 选择「在浏览器打开」
3. 选择Chrome浏览器
4. 即可使用完整录音功能
    `;
  }
}

// Create singleton instance
export const platformService = new PlatformService();